// scheduleTime.js — a start time is a MOMENT, not a day.
//
// This is the opposite rule to `fmtDate` in InvoiceManagement.jsx, and
// deliberately so. A due date is a calendar DAY: it is stored as UTC midnight
// and rendered in UTC, so it reads back as the day that was typed no matter
// where the browser is (see services/invoiceDueDate for the server half). A
// start time is an INSTANT — "begin chasing on the 15th at 09:00" means 09:00
// where the person setting it is sitting — so it round-trips through the local
// clock instead.
//
// Its own module rather than another export from InvoiceManagement.jsx, because
// ReminderPreview needs it and InvoiceManagement already imports
// ReminderPreview: taking them from there would close a require cycle.

/**
 * Format an instant for a `<input type="datetime-local">`.
 *
 * `toISOString().slice(0, 16)` is the trap this exists to avoid. It produces a
 * UTC wall-clock string, which the input then reads as LOCAL — so an operator
 * west of UTC opens a reminder scheduled for 09:00 and finds it showing 04:00,
 * and saving it moves the send. That bug was live in the review panel's
 * "Sends on" box.
 */
export function toLocalInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Is this start still ahead of us?
 *
 * A start that has passed means exactly the same as no start at all: the flow
 * goes as soon as sending is switched on. The UI has to say that rather than
 * showing a date nothing will wait for — an operator who reads "starts 3 weeks
 * ago" next to a loop that just sent has been told nothing useful.
 */
export function startIsPending(value, now = new Date()) {
  if (!value) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) && d.getTime() > now.getTime();
}

/**
 * What a reminder loop is actually doing right now.
 *
 * Five states, not two, because "sendingEnabled" answers a different question
 * from "is anything going to happen today":
 *
 *   settled — payment confirmed. Finished; the toggle is inert.
 *   armed   — switched ON, holding for a start that has not arrived. Nothing
 *             has been emailed and nothing will be until the date.
 *   on      — switched ON and due. The next tick sends.
 *   waiting — switched OFF, but carrying a start still ahead of it. Turning it
 *             on will arm rather than send.
 *   off     — switched OFF, no start chosen. Turning it on sends.
 *
 * `armed` and `on` are the pair that matters. Both are sendingEnabled, both
 * would read "● ON", and on the day they mean opposite things — which is how
 * an operator concludes a correctly scheduled chase is broken. A single
 * function so the table, the toggle title and the confirm dialog cannot each
 * decide it differently.
 */
export function reminderSendState(reminder, now = new Date()) {
  const r = reminder || {};
  if (r.paymentConfirmed) return 'settled';
  if (r.sendingEnabled) return startIsPending(r.nextReminderAt, now) ? 'armed' : 'on';
  return startIsPending(r.scheduledStartAt, now) ? 'waiting' : 'off';
}

// ── THE START IN A CHOSEN TIMEZONE (the user, 2026-10-15) ───────────────────
//
// "Begin at 09:00" is usually 09:00 where the CLIENT is, not where the person
// setting it sits. The form now picks a zone (default: the browser's) and the
// typed wall clock is read in THAT zone. The loop stores the instant
// (scheduledStartAt) and the zone it was chosen in (scheduleTimeZone), so it
// reads back as the same 09:00 wherever it is opened.

/** The browser's own IANA zone, e.g. "America/New_York". */
export function browserTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { return 'UTC'; }
}

export function isValidTimeZone(tz) {
  if (!tz) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch (_) { return false; }
}

const COMMON_ZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto',
  'America/Mexico_City', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Africa/Tunis', 'Africa/Cairo', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kolkata', 'Asia/Singapore',
  'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney',
];

/** Every zone the browser knows (common list when it cannot say), always including `extra`. */
export function timeZoneOptions(...extra) {
  let all = [];
  try { if (typeof Intl.supportedValuesOf === 'function') all = Intl.supportedValuesOf('timeZone'); } catch (_) { all = []; }
  if (!all.length) all = COMMON_ZONES;
  const out = new Set(all);
  out.add('UTC');
  for (const z of extra) if (z && isValidTimeZone(z)) out.add(z);
  return [...out].sort((a, b) => (a === 'UTC' ? -1 : b === 'UTC' ? 1 : a.localeCompare(b)));
}

// The wall clock an instant shows in `tz`, as UTC milliseconds — the
// difference from the instant itself is the zone's offset at that moment.
function wallClockMs(ms, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (t) => Number((parts.find((p) => p.type === t) || {}).value);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
}

/**
 * "2026-10-16T09:00" read as a wall clock in `tz` → the ISO instant. Two
 * passes so a start either side of a DST change lands on the right offset.
 * '' for an empty or unreadable box.
 */
export function zonedInputToIso(local, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(local || ''));
  if (!m) return '';
  const zone = isValidTimeZone(tz) ? tz : 'UTC';
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let t = guess - (wallClockMs(guess, zone) - guess);
  t = guess - (wallClockMs(t, zone) - t);
  return new Date(t).toISOString();
}

/** An instant, shown in a datetime-local box as the wall clock in `tz`. */
export function toZonedInputValue(value, tz) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const zone = isValidTimeZone(tz) ? tz : 'UTC';
  return new Date(wallClockMs(d.getTime(), zone)).toISOString().slice(0, 16);
}

/** "Oct 16, 2026, 9:00 AM EDT" in `tz` (the browser's own zone when none was stored). */
export function fmtInZone(value, tz) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const zone = isValidTimeZone(tz) ? tz : undefined;
  return d.toLocaleString('en-US', {
    timeZone: zone, year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}
